import {
  CustomFormApiTypes,
  FormUpdateAttribute,
  UpdateCustomFormArg,
} from 'apis/customForms/types';

import {
  CustomForm,
  CustomFormField,
  externalReferenceFromEmail,
  externalReferenceName,
  externalReferenceSubject,
} from '../../customForms.types';

export const formObjectConverter = {
  toInternal: transformApiTypesToInternalTypes,
  toApi: transformInternalTypesToApiTypes,
};

function convertToCustomField(attribute: FormUpdateAttribute): CustomFormField {
  const common = {
    id: attribute.id,
    title: attribute.label,
    required: attribute.is_required ?? false,
    placeholder: attribute.place_holder ?? '',
  };

  switch (attribute.type) {
    case 'text': {
      if (attribute.external_reference === externalReferenceFromEmail) {
        return {
          ...common,
          type: 'email',
        };
      }

      if (attribute.external_reference === externalReferenceName) {
        return {
          ...common,
          type: 'name',
        };
      }

      if (attribute.external_reference === externalReferenceSubject) {
        return {
          ...common,
          type: 'subject',
        };
      }

      return {
        ...common,
        type: 'text',
      };
    }

    case 'text_area': {
      return {
        ...common,
        type: 'textarea',
      };
    }

    case 'select': {
      return {
        ...common,
        type: 'select',
        options: attribute.options?.map((option) => ({
          value: option.value,
          label: option.label,
        })),
      };
    }

    case 'file_multiple': {
      return {
        ...common,
        type: 'attachments',
      };
    }

    case 'file_single': {
      throw new Error(`Attribute type ${attribute.type} is not supported.`);
    }
  }
}

export function transformApiTypesToInternalTypes(
  customForm: CustomFormApiTypes['Form'],
): CustomForm {
  return {
    id: customForm.id,
    title: customForm.label,
    submitButtonLabel: customForm.submit_button_label,
    feedbackAfterSubmit: customForm.submit_success_message,
    usage: [], // `usage` is only present on the form listing endpoint
    fields: customForm.attributes?.map(convertToCustomField) ?? [],
    hasAttributes: (customForm.attributes?.length || 0) > 0,
  };
}

function convertToFormUpdateAttribute(
  field: CustomFormField,
): FormUpdateAttribute {
  const common = {
    entity_id: 'message',
    identifier: field.id,
    is_required: field.required,
    place_holder: field.placeholder,
    label: field.title,
    id: field.id,
    options: [],
  };

  switch (field.type) {
    case 'text':
      return {
        ...common,
        type: 'text',
      };

    case 'textarea':
      return {
        ...common,
        type: 'text_area',
      };

    case 'select':
      return {
        ...common,
        type: 'select',
        options:
          field.options?.map((option) => ({
            label: option.value,
            value: option.value,
            sub_options: [],
          })) ?? [],
      };

    case 'attachments':
      return {
        ...common,
        type: 'file_multiple',
      };

    case 'email':
      return {
        ...common,
        type: 'text',
        external_reference: externalReferenceFromEmail,
      };

    case 'name':
      return {
        ...common,
        type: 'text',
        external_reference: externalReferenceName,
      };

    case 'subject':
      return {
        ...common,
        type: 'text',
        external_reference: externalReferenceSubject,
      };
  }
}

export function transformInternalTypesToApiTypes(
  form: CustomForm,
): UpdateCustomFormArg {
  return {
    id: form.id,
    label: form.title,
    description: '', // description is supported by API but not used in UI
    submit_button_label: form.submitButtonLabel,
    submit_success_message: form.feedbackAfterSubmit,
    attributes: form.fields.map(convertToFormUpdateAttribute),
  };
}
